/**
 * `FleetApi` over HTTP, against `console-api` (wf-12, step 4).
 *
 * TWO THINGS THIS FILE IS THE ONLY PLACE FOR, and both are cases:
 *
 *  - The bearer token is attached HERE, once, for every request (U-11). Attached by each
 *    caller instead, it is attached by every caller except the one added on a Friday, and
 *    that call either fails in a way somebody "fixes" by widening the API or succeeds
 *    because a route was not scoped. One choke point, and the method list it is asserted
 *    over is exhaustive by construction.
 *  - A `401` becomes a re-authentication signal (U-12). It is never an empty list.
 *
 * WHAT IS ASSUMED HERE, and it is the finding this lane reports. `console-api` is a separate
 * application from `fleet-api` — deliberately, so that a user chain and a deployment chain
 * never sit in one process (wf-01 U-22) — and it has no published contract and no component
 * file. `docs/plan/contracts/openapi.yaml` describes the fourteen DEPLOYMENT-facing paths and
 * none of these, so nothing in this repo can assert the paths below are right. They are
 * therefore declared in ONE map, with the prefix chosen to be visibly not `fleet-api`'s
 * `/fleet/v1`. When `console-api` publishes a contract and a fixture, this map is the only
 * thing that changes and a contract test replaces the assumption. That is not the same as
 * calling an endpoint that is not in the contract, which `CLAUDE.md` forbids and which would
 * mean pointing this at `fleet-api`.
 */
import {
  CONSOLE_CLIENT_PATHS,
  ConsoleRefusal,
  DEFAULT_INSTALL_KIND,
  DEFAULT_PRODUCT,
  deploymentNameOf,
  isLegacyProduct,
  isManagedApplication,
  marketplacePlanOf,
  parseConsoleError,
  patchBody,
  type BundleSummary,
  type ClientCreate,
  type ClientPatch,
  type ClientReadiness,
  type ClientRecord,
  type InstallCreate,
  type InstallRecord,
  type IssuedBundle,
} from './clients.ts'
import { FleetApiAuthError, FleetApiError } from './errors.ts'
import { ENVIRONMENT_CLASSES, type EnvironmentClass } from '../screens/fleet/api/contract.ts'
import type { Awaitable, FleetApi } from './fleet-api.ts'
import type {
  DeploymentMarketplacePlan,
  DeploymentTargetRelease,
  FleetReleaseNoticeRecord,
  InstallPackageRequest,
  InstallPackageResult,
  ManagedAppReleaseRecord,
  ManagedUpdate,
  MarketplacePlan,
  ReleaseNoticeRecord,
  ReleaseNoticeResult,
  OfferPreview,
  ReleaseNoticeSet,
  TargetRelease,
  DeploymentHistoryEntry,
  FleetHistoryEntry,
  UninstallPackageRequest,
  UpgradePackageRequest,
  UpgradePackageResult,
} from './clients.ts'
import type {
  DefinitionRecord,
  DeploymentRecord,
  EvalPass,
  PublishResult,
  PushRecord,
  RelayCadence,
  RelayCommand,
  RelayComponentStatus,
  RelayDirective,
  RelayExceptions,
  RelayExceptionUpload,
  RelayHeartbeat,
  RelayTelemetry,
  RelayTelemetryCommand,
  RelayTelemetryPage,
  RelayTelemetrySummary,
} from './records.ts'
import type { HeartbeatState, IssuedInstruction, RestartRecord } from '../screens/fleet/model/heartbeat.ts'
import type { FleetAuditEvent } from './admin.ts'
import type { DeploymentHold, Installation, InstallationChange, PushOutcome, ReportedNaming } from './records.ts'
import type { ReasonClass } from '../screens/fleet/api/contract.ts'
import type { DeliveryAttempt } from '../screens/fleet/model/delivery.ts'
import type {
  AssignmentCreate,
  FleetPayload,
  ConsoleAuditEvent,
  ConsoleRole,
  ConsoleUser,
  CurrentAccess,
  RoleAssignment,
  RoleCreate,
  RolePatch,
  UserInvite,
  UserPatch,
} from './admin.ts'

/** How the client asks for a token. The auth lane supplies it; this file never mints one. */
/**
 * Where the bearer comes from. `forceRefresh` is asked once, after console-api refused a token,
 * so a lapsed sign-in is renewed and the request retried before the screen says signed out (#215).
 */
export type TokenSource = (options?: { forceRefresh?: boolean }) => Awaitable<string | null>

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

export interface HttpFleetApiOptions {
  /** Origin plus optional prefix, no trailing slash. `configuredBaseUrl()` normalises it. */
  baseUrl: string
  getToken: TokenSource
  /** Called before the auth error is thrown, so a sign-in surface can react once. */
  onUnauthorized?: () => void
  fetchImpl?: FetchLike
}

/**
 * The deployment shape currently published by console-api.
 *
 * It is intentionally smaller than `DeploymentRecord`: the service stores fleet metadata, not
 * copies of customer payloads, so connector coverage and run telemetry are not present on this
 * response. Keeping this shape separate stops a TypeScript cast from turning absent wire fields
 * into required screen fields at runtime.
 */
interface ConsoleDeployment {
  id: string
  /**
   * The frozen reconciliation object, 31 August. OPTIONAL ON THE WIRE TYPE and nullable inside,
   * because console-api is deploying it after this console - a record that predates it simply
   * has no key, and the mapper turns that into `null` rather than into a shape of zeroes.
   */
  reconciliation?: {
    bootEpoch: string | null
    fromSequence: number | null
    committedSequence: number | null
    refusedCount: number | null
    reportedSequence: number | null
    reportedEpoch: string | null
    gapSince: string | null
  } | null
  clientId: string
  clientSlug: string
  clientName: string
  environmentClass: string
  /**
   * Copied from the redeemed bundle at enrollment. OPTIONAL ON THE WIRE TYPE for the same reason
   * as `reconciliation` above: a console-api that predates naming sends no key, and the mapper
   * turns that into `null`, which is the install package's legacy path and not an error.
   */
  namePrefix?: string | null
  nameSuffix?: string | null
  /**
   * Managed applications (D50). Optional on the wire type for the same reason as the naming: a
   * console-api that predates them sends no key, which the contract defaults to `customer-deploy`
   * and a `null` naming environment.
   */
  installKind?: string
  namingEnvironment?: string | null
  /**
   * `nexus-prod` or `nexus-client-tenant`. Optional on the wire type for the same reason: a
   * console-api that predates it sends no key, which maps to `DEFAULT_PRODUCT`.
   */
  product?: string
  /** Optional on the wire type for the same reason as the naming: an older console-api omits it. */
  resourceGroup?: string | null
  expectedResourceGroup?: string | null
  /** Optional on the wire type for the same reason: a console-api before 0034 omits it. */
  reportedNaming?: ReportedNaming | null
  /** Optional on the wire type for the same reason: a console-api before 0040 omits both. */
  marketplacePlanId?: string | null
  marketplacePlan?: string | null
  /** Optional on the wire type for the same reason: a console-api before 0042 omits all four. */
  declaredMarketplacePlan?: string | null
  declaredMarketplacePlanBy?: string | null
  declaredMarketplacePlanAt?: string | null
  effectiveMarketplacePlan?: string | null
  /** Optional on the wire type for the same reason: a console-api before fleet-api#192 omits it. */
  targetRelease?: DeploymentTargetRelease | null
  /** Optional on the wire type for the same reason: a console-api before fleet-api#185 omits all three. */
  marketplaceOfferId?: string | null
  consoleOrigin?: string | null
  apiOrigin?: string | null
  /** Optional on the wire type for the same reason: a console-api before fleet-api#264 omits it. */
  installation?: Installation | null
  /** Reported by the deployment, `null` when not reported (console-api `DeploymentOut`). */
  reportedSnapshotId?: string | null
  driftCount?: number | null
  tenantId: string
  subscriptionId: string | null
  region: string | null
  enrolledAt: string | null
  // `enrollmentState`, two l's: console-api sends it as enrollmentState (routes/fleet.py:194).
  // Dead today - deploymentRecord below never reads it - which is exactly why it survived the
  // rename. A declared field the server never sends is a landmine rather than a bug: it is
  // `undefined` the first time somebody renders it, and an empty cell reads as "no data yet".
  enrollmentState: string
  healthRollup: string | null
  pinnedRelayVersion: string | null
  snapshotId: string | null
  snapshotSequence: number | null
  lastHeartbeatAt: string | null
  /** `null` when not held. See `DeploymentHold` - one check, no pointer arithmetic. */
  hold: DeploymentHold | null
  credentialExpiresAt: string | null
  decommissionedAt: string | null
  components?: RelayComponentStatus[]
  telemetrySummary?: RelayTelemetrySummary[]
  /**
   * `POST /relay/heartbeat`'s last arrival. OPTIONAL ON THE WIRE TYPE, like `components` above
   * and for the same reason: a console-api that predates this key sends no key at all, and the
   * mapper turns that absence into `null` - "never received", not "received nothing".
   */
  relayHeartbeat?: ConsoleRelayHeartbeat | null
  relayCadence: RelayCadence
}

/**
 * `warning`, `conditionDetails` and `mirror` are optional on the wire for the same reason: a
 * console-api that predates them sends none of them, and the mapper reads that as "reported none".
 */
type ConsoleRelayHeartbeat = Omit<RelayHeartbeat, 'warning' | 'conditionDetails' | 'mirror'> &
  Partial<Pick<RelayHeartbeat, 'warning' | 'conditionDetails' | 'mirror'>>

/**
 * One push as console-api serves it (`PushOut`). Not the console's `PushRecord`: it carries the
 * item COUNT and not the items, a combined `reason`, and `sent` where the console says `pending`.
 * The console used to cast one to the other, and a real row would have thrown on `items.length`.
 */
interface ConsolePush {
  id: string
  deploymentId: string
  snapshotId: string
  sentAt: string
  actor: string
  outcome: string
  /** `class: detail` on a rolled-back push, as the ack wrote it. */
  reason: string | null
  itemCount: number | null
}

function pushRecord(p: ConsolePush): PushRecord {
  const outcome: PushOutcome = p.outcome === 'applied' || p.outcome === 'rolled-back' ? p.outcome : 'pending'
  const record: PushRecord = {
    pushId: p.id,
    deploymentId: p.deploymentId,
    // Every console-api push is one the Relay acked. A revert is a console action, which the
    // deployment's History shows; console-api does not record one as a push.
    direction: 'publish',
    at: p.sentAt,
    actor: p.actor,
    reason: p.reason ?? '',
    snapshotId: p.snapshotId,
    previousSnapshotId: null,
    items: [],
    outcome,
    itemCount: p.itemCount,
  }
  if (outcome === 'rolled-back' && p.reason) {
    const [reasonClass, ...detail] = p.reason.split(': ')
    record.reasonClass = reasonClass as ReasonClass
    if (detail.length) record.reasonDetail = detail.join(': ')
  }
  return record
}

/**
 * A notice as the screens read it. `withdrawnAt` and `withdrawnBy` are required by the contract; a
 * console-api from before withdrawal was recorded sends neither, and that reads as an offer that
 * stands (null) rather than as undefined, which no screen has a word for.
 */
function noticeOf(notice: ReleaseNoticeRecord): ReleaseNoticeRecord {
  return { ...notice, withdrawnAt: notice.withdrawnAt ?? null, withdrawnBy: notice.withdrawnBy ?? null }
}

/**
 * The keys a deployment row's target carries, and only those. `imageOnly` arrived with major updates
 * (nth-ai/fleet-api#225): a console-api that predates it sends none, and every target it set was
 * image-only.
 */
function deploymentTargetOf(record: DeploymentTargetRelease | null): DeploymentTargetRelease | null {
  if (!record) return null
  return { version: record.version, imageOnly: record.imageOnly ?? true, setAt: record.setAt, setBy: record.setBy }
}

/**
 * A target release as the console reads it. `notes` arrived with release notices
 * (nth-ai/fleet-api#187): a console-api that predates it sends no key, read as `null` rather than
 * left undefined, so "none copied" is one value.
 */
function targetReleaseOf(record: TargetRelease): TargetRelease {
  return { ...record, notes: record.notes ?? null, imageOnly: record.imageOnly ?? true }
}

function isEnvironmentClass(value: string): value is EnvironmentClass {
  return (ENVIRONMENT_CLASSES as readonly string[]).includes(value)
}

function deploymentRecord(record: ConsoleDeployment): DeploymentRecord {
  // The set is the CONTRACT's, imported rather than restated here - see `ENVIRONMENT_CLASSES`
  // for what restating it cost. The refusal stays: a class we cannot name is still a value we
  // cannot render, and `deploymentName` below is that string.
  if (!isEnvironmentClass(record.environmentClass)) {
    throw new FleetApiError(
      `console-api returned unknown environment class ${record.environmentClass}, ` +
        `and the contract enumerates ${ENVIRONMENT_CLASSES.join(', ')}`,
      200,
      CONSOLE_API_PATHS.deployments,
    )
  }

  return {
    deploymentId: record.id,
    clientId: record.clientId,
    clientName: record.clientName,
    // console-api has no separately named deployment, so the label is derived: the naming the
    // deployment inherited (`<prefix>-<env>-<suffix>`, what its resource names are built from and
    // what tells two of one client's deployments apart, D39), else the environment class for a
    // deployment enrolled before naming was stored. See `deploymentNameOf`.
    deploymentName: deploymentNameOf(record),
    environmentClass: record.environmentClass,
    // Only the one value that changes behaviour is passed through; anything else, absence
    // included, is the contract's default.
    installKind: isManagedApplication(record) ? 'managed-application' : DEFAULT_INSTALL_KIND,
    // The same device: only the legacy value changes behaviour (no install or upgrade package).
    product: isLegacyProduct(record) ? 'nexus-client-tenant' : DEFAULT_PRODUCT,
    namePrefix: record.namePrefix ?? null,
    nameSuffix: record.nameSuffix ?? null,
    namingEnvironment: record.namingEnvironment ?? null,
    // `null` for a managed application, whose resource group the customer's Portal form named.
    resourceGroup: record.resourceGroup ?? null,
    expectedResourceGroup: record.expectedResourceGroup ?? null,
    reportedNaming: record.reportedNaming ?? null,
    marketplacePlanId: record.marketplacePlanId ?? null,
    // A kind this console does not know is `null`, "not known", rather than either of the two.
    marketplacePlan: marketplacePlanOf(record.marketplacePlan),
    declaredMarketplacePlan: marketplacePlanOf(record.declaredMarketplacePlan),
    declaredMarketplacePlanBy: record.declaredMarketplacePlanBy ?? null,
    declaredMarketplacePlanAt: record.declaredMarketplacePlanAt ?? null,
    // console-api decides it, in one place. One before 0042 sends no key and can hold no
    // declaration, so its effective plan is the reported one, which is its own rule for that case.
    effectiveMarketplacePlan: marketplacePlanOf(
      record.effectiveMarketplacePlan === undefined ? record.marketplacePlan : record.effectiveMarketplacePlan,
    ),
    // Absent stays absent: a console-api before fleet-api#192 does not say, which is not "none".
    ...(record.targetRelease === undefined ? {} : { targetRelease: deploymentTargetOf(record.targetRelease) }),
    tenantId: record.tenantId,
    subscriptionId: record.subscriptionId ?? '',
    region: record.region ?? '',
    enrolledAt: record.enrolledAt,
    credentialExpiresAt: record.credentialExpiresAt,
    egressLogUrl: null,
    lastState: null,
    lastSeenAt: record.lastHeartbeatAt,
    // Mapped explicitly rather than defaulted. fleet-api shipped this field on their record and
    // forgot to teach their own response builder about it, so every response carried
    // `hold: null` however the row actually read - the route worked, the store worked, and the
    // wire lied. A `?? null` here would reproduce that on our side and be indistinguishable
    // from a deployment that genuinely is not held.
    hold: record.hold,
    sequence: 0,
    /**
     * MAPPED THROUGH, NOT DEFAULTED. `record.reconciliation` is the frozen object console-api
     * serves; absent it, `null` - which the reading turns into `could-not-read` rather than into
     * agreement between two absences. Defaulting the inner fields to 0 here would make "never
     * reported" unrepresentable before any screen got a chance to render it honestly, which is
     * the same absent-is-not-zero failure that made `sequence` above a `number`.
     */
    reconciliation: record.reconciliation ?? null,
    relayComponents: record.components ?? [],
    relayTelemetrySummary: record.telemetrySummary ?? [],
    // These arrays describe payload detail that this metadata-only response does not carry.
    // Empty means "none available from this response", and the surfaces render that as —.
    connectors: [],
    runs: [],
    pipelines: [],
    // Absent on the wire (an older console-api) and reported-null (never received) both mean
    // the same thing to this screen, so both collapse to `null` here rather than three states
    // the rest of the console would have to learn to tell apart.
    relayHeartbeat: record.relayHeartbeat
      ? {
          ...record.relayHeartbeat,
          warning: record.relayHeartbeat.warning ?? [],
          conditionDetails: record.relayHeartbeat.conditionDetails ?? [],
          mirror: record.relayHeartbeat.mirror ?? null,
        }
      : null,
    relayCadence: record.relayCadence,
    librarySnapshotId: record.snapshotId ?? '',
    libraryDefinitionSetVersion: record.snapshotSequence ?? 0,
    previousSnapshotId: null,
    pinnedRelayVersion: record.pinnedRelayVersion ?? '',
    marketplaceOfferId: record.marketplaceOfferId ?? null,
    consoleOrigin: record.consoleOrigin ?? null,
    apiOrigin: record.apiOrigin ?? null,
    installation: record.installation ?? null,
    decommissionedAt: record.decommissionedAt ?? null,
    reportedSnapshotId: record.reportedSnapshotId ?? null,
    driftCount: record.driftCount ?? null,
  }
}

/** The console-api surface this console reads. Assumed, in one place, until it is published. */
const CONSOLE_API_PATHS = {
  deployments: '/console/v1/deployments',
  deployment: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}`,
  pushes: '/console/v1/pushes',
  history: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/history`,
  fleetHistory: '/console/v1/history',
  // The heartbeat channel. Three reads, not one: liveness, restart history and the serve
  // record answer different questions, and a single blob endpoint would invite a screen to
  // render them as one signal.
  heartbeat: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/heartbeat`,
  restarts: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/restarts`,
  installationChanges: (id: string) =>
    `/console/v1/deployments/${encodeURIComponent(id)}/installation-changes`,
  relayTelemetry: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-telemetry`,
  instructions: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/instructions`,
  definitions: '/console/v1/definitions',
  kinds: '/console/v1/definition-kinds',
  publish: (kind: string, key: string) =>
    `/console/v1/definitions/${encodeURIComponent(kind)}/${encodeURIComponent(key)}/publish`,
  evalPass: (kind: string, key: string) =>
    `/console/v1/definitions/${encodeURIComponent(kind)}/${encodeURIComponent(key)}/eval-pass`,
  revert: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/revert`,
  // Relay exception handling: the switch, the commands, and the uploads they produced.
  relayExceptions: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-exceptions`,
  relayProfile: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-exceptions/profile`,
  relayCommands: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-exceptions/commands`,
  relayUploads: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-exceptions/uploads`,
  relayUpload: (id: string, uploadId: string) =>
    `/console/v1/deployments/${encodeURIComponent(id)}/relay-exceptions/uploads/${encodeURIComponent(uploadId)}`,
  relayTelemetryPause: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-telemetry-pause`,
  relayDirectives: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-directives`,
  relayCadence: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/relay-cadence`,
  // The client half. Declared in `clients.ts` beside the shapes they carry and re-exported
  // through here so this map stays the one place a path is written down.
  ...CONSOLE_CLIENT_PATHS,
  currentAccess: '/console/v1/access/me',
  capabilities: '/console/v1/access/capabilities',
  users: '/console/v1/users',
  user: (id: string) => `/console/v1/users/${encodeURIComponent(id)}`,
  roles: '/console/v1/roles',
  role: (id: string) => `/console/v1/roles/${encodeURIComponent(id)}`,
  assignments: '/console/v1/role-assignments',
  assignment: (id: string) => `/console/v1/role-assignments/${encodeURIComponent(id)}`,
  audit: '/console/v1/audit',
  fleetAudit: '/console/v1/fleet-audit',
  fleetPayload: (hash: string) => `/console/v1/fleet-payload/${encodeURIComponent(hash)}`,
  delivery: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/delivery`,
  telemetry: (id: string) => `/console/v1/deployments/${encodeURIComponent(id)}/telemetry`,
} as const

/** A Relay command's optional reason as console-api takes it: trimmed, and a blank one left off. */
function reasonField(reason: string | undefined): { reason?: string } {
  const text = reason?.trim()
  return text ? { reason: text } : {}
}

/** The body of a response we already know is a failure, or null if it is not JSON at all. */
async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

export function createHttpFleetApi(options: HttpFleetApiOptions): FleetApi {
  const base = options.baseUrl.replace(/\/+$/, '')
  const doFetch: FetchLike = options.fetchImpl ?? ((input, init) => fetch(input, init))

  function url(path: string, query?: Record<string, string | undefined>): string {
    const search = new URLSearchParams()
    for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined) search.set(k, v)
    const qs = search.toString()
    const full = `${base}${path}${qs ? `?${qs}` : ''}`
    // Belt and braces for U-16: the token goes to console-api and nowhere else, so a path
    // that somehow resolved off the configured base must not be reached with one attached.
    if (!full.startsWith(`${base}/`)) {
      throw new FleetApiError(`refusing to send a console-api call to ${full}`, 0, full)
    }
    return full
  }

  async function send<T>(target: string, init?: RequestInit): Promise<T | null> {
    const token = await options.getToken()
    if (!token) {
      // No principal is the same answer as a rejected one: sign in. Not an empty result.
      options.onUnauthorized?.()
      throw new FleetApiAuthError('no access token: sign in to reach the fleet', 401, target)
    }

    const call = (bearer: string) => {
      const headers = new Headers(init?.headers)
      headers.set('Authorization', `Bearer ${bearer}`)
      headers.set('Accept', 'application/json')
      if (init?.body !== undefined) headers.set('Content-Type', 'application/json')
      return doFetch(target, { ...init, headers })
    }

    let response = await call(token)

    if (response.status === 401) {
      // ONCE, with a renewed token, before saying signed out: a sign-in that lapsed while the
      // tab was open is renewed silently rather than shown as NOT SIGNED IN on every page (#215).
      const renewed = await options.getToken({ forceRefresh: true })
      if (renewed && renewed !== token) response = await call(renewed)
    }
    if (response.status === 401) {
      options.onUnauthorized?.()
      throw new FleetApiAuthError('console-api rejected the token: sign in again', 401, target)
    }
    // 404 is also what an unentitled read returns, on purpose: a 403 confirms the thing
    // exists (wf-12 U-20). The console cannot tell the two apart and must not pretend to.
    if (response.status === 404) return null
    if (!response.ok) {
      /*
       * A REFUSAL THAT NAMES WHAT IS MISSING IS NOT THE SAME AS A FAILURE, and this is the
       * only place that distinction can be made.
       *
       * `console-api`'s envelope carries a `missing` array that the fleet `Error` shape does
       * not have, because `CONSOLES.md` §2.3 requires a screen to render the failed check by
       * name rather than grey out a button. Throwing a bare `FleetApiError` here would parse
       * the response successfully and discard the only part of it worth showing an operator —
       * the screen would then have a 409 and nothing to say about it, which is the "not ready
       * with no reason" that §2.3 calls a support call.
       *
       * The body is read defensively: a proxy or a gateway can return a non-JSON 502, and a
       * transport that threw while parsing an error would replace a useful status code with a
       * SyntaxError.
       */
      const refusal = parseConsoleError(await safeJson(response))
      if (refusal) throw new ConsoleRefusal(refusal, response.status, target)
      throw new FleetApiError(`console-api returned ${response.status} for ${target}`, response.status, target)
    }
    if (response.status === 204) return null
    return (await response.json()) as T
  }

  /** A POST whose answer is a zip rather than JSON: the install and upgrade package downloads. */
  async function downloadZip(path: string, input: unknown, clientId: string): Promise<Blob> {
    const token = await options.getToken()
    if (!token) {
      options.onUnauthorized?.()
      throw new FleetApiAuthError('no access token: sign in to reach the fleet', 401, clientId)
    }
    const target = url(path)
    const call = (bearer: string) =>
      doFetch(target, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearer}`,
        },
        body: JSON.stringify(input),
      })
    let response = await call(token)
    if (response.status === 401) {
      // Once, renewed, as `send` does (#215).
      const renewed = await options.getToken({ forceRefresh: true })
      if (renewed && renewed !== token) response = await call(renewed)
    }
    if (response.status === 401) {
      options.onUnauthorized?.()
      throw new FleetApiAuthError('console-api rejected the token: sign in again', 401, target)
    }
    if (!response.ok) {
      const detail = await safeJson(response)
      throw new FleetApiError(
        typeof detail === 'object' && detail && 'detail' in detail ? String((detail as { detail: unknown }).detail) : `download failed (${response.status})`,
        response.status,
        clientId,
      )
    }
    return response.blob()
  }

  /** A GET whose answer is a file rather than JSON: a Relay exception upload. */
  async function downloadBlob(target: string): Promise<Blob> {
    const token = await options.getToken()
    if (!token) {
      options.onUnauthorized?.()
      throw new FleetApiAuthError('no access token: sign in to reach the fleet', 401, target)
    }
    const response = await doFetch(target, { headers: { Authorization: `Bearer ${token}` } })
    if (response.status === 401) {
      options.onUnauthorized?.()
      throw new FleetApiAuthError('console-api rejected the token: sign in again', 401, target)
    }
    if (!response.ok) {
      const refusal = parseConsoleError(await safeJson(response))
      if (refusal) throw new ConsoleRefusal(refusal, response.status, target)
      throw new FleetApiError(`console-api returned ${response.status} for ${target}`, response.status, target)
    }
    return response.blob()
  }

  /** A read that must produce a list. An empty list is a fact; a missing one is a failure. */
  /**
   * A single object that must be there, as opposed to `send`, which may answer `null`.
   *
   * Deliberately not returning `T | null`: a nullable heartbeat state invites `?? {}` or a
   * default object at the call site, and a defaulted heartbeat is a deployment reported as
   * silent-and-fine when the truth is that we could not read it. Absent is not zero applies to
   * the whole record, not only to its fields. A deployment that does not exist is an error.
   */
  async function readOne<T>(target: string): Promise<T> {
    const body = await send<T>(target)
    if (body === null || typeof body !== 'object') {
      throw new FleetApiError(`console-api returned no record for ${target}`, 200, target)
    }
    return body
  }

  async function readList<T>(target: string): Promise<T[]> {
    const body = await send<T[]>(target)
    if (!Array.isArray(body)) {
      throw new FleetApiError(`console-api returned no list for ${target}`, 200, target)
    }
    return body
  }

  return {
    listDeployments: async () =>
      (await readList<ConsoleDeployment>(url(CONSOLE_API_PATHS.deployments))).map(deploymentRecord),

    getDeployment: async (deploymentId) => {
      const record = await send<ConsoleDeployment>(url(CONSOLE_API_PATHS.deployment(deploymentId)))
      return record ? deploymentRecord(record) : null
    },

    archiveDeployment: async (deploymentId) => {
      const record = await send<ConsoleDeployment>(url(CONSOLE_API_PATHS.deployment(deploymentId)), {
        method: 'PATCH',
        body: JSON.stringify({ archived: true }),
      })
      if (!record) throw new FleetApiError(`no deployment ${deploymentId}`, 404, deploymentId)
    },

    listPushes: async (deploymentId) =>
      (await readList<ConsolePush>(url(CONSOLE_API_PATHS.pushes, { deploymentId }))).map(pushRecord),
    getFleetHistory: (query = {}) =>
      readList<FleetHistoryEntry>(
        url(CONSOLE_API_PATHS.fleetHistory, {
          clientId: query.clientId,
          limit: query.limit === undefined ? undefined : String(query.limit),
        }),
      ),
    getDeploymentHistory: (deploymentId, limit) =>
      readList<DeploymentHistoryEntry>(
        url(CONSOLE_API_PATHS.history(deploymentId), { limit: limit === undefined ? undefined : String(limit) }),
      ),

    heartbeatState: (deploymentId) => readOne<HeartbeatState>(url(CONSOLE_API_PATHS.heartbeat(deploymentId))),
    listRestarts: (deploymentId) => readList<RestartRecord>(url(CONSOLE_API_PATHS.restarts(deploymentId))),
    listInstallationChanges: (deploymentId) =>
      readList<InstallationChange>(url(CONSOLE_API_PATHS.installationChanges(deploymentId))),
    deploymentRelayTelemetry: (deploymentId) =>
      readOne<RelayTelemetry>(url(CONSOLE_API_PATHS.relayTelemetry(deploymentId))),
    listInstructions: (deploymentId) =>
      readList<IssuedInstruction>(url(CONSOLE_API_PATHS.instructions(deploymentId))),

    listDefinitions: (kind) => readList<DefinitionRecord>(url(CONSOLE_API_PATHS.definitions, { kind })),

    kinds: () => readList<string>(url(CONSOLE_API_PATHS.kinds)),

    publishDefinition: async (kind, key) => {
      const result = await send<PublishResult>(url(CONSOLE_API_PATHS.publish(kind, key)), {
        method: 'POST',
        body: JSON.stringify({}),
      })
      if (!result) throw new FleetApiError(`no definition ${kind}/${key}`, 404, kind)
      return result
    },

    recordEvalPass: async (kind, key, pass: EvalPass) => {
      await send<void>(url(CONSOLE_API_PATHS.evalPass(kind, key)), {
        method: 'POST',
        body: JSON.stringify(pass),
      })
    },

    revertToPreviousSnapshot: async (deploymentId, actor, reason) => {
      // `toSnapshotId` is deliberately NOT sent. The fleet resolves which snapshot is
      // previous, anchored on what the deployment holds rather than on the fleet's newest;
      // sending our own guess would revert relative to the wrong thing for any deployment that
      // is already behind. The parameter exists for a future screen that picks one explicitly.
      const record = await send<ConsoleDeployment>(url(CONSOLE_API_PATHS.revert(deploymentId)), {
        method: 'POST',
        body: JSON.stringify({ reason }),
      })
      if (!record) throw new FleetApiError(`no deployment ${deploymentId}`, 404, deploymentId)
      // `actor` is not sent: console-api takes it from the bearer token, and a client-supplied
      // actor on an audited action is a field somebody can set to anything.
      void actor
      return deploymentRecord(record)
    },

    releaseHold: async (deploymentId) => {
      const record = await send<ConsoleDeployment>(url(CONSOLE_API_PATHS.revert(deploymentId)), { method: 'DELETE' })
      if (!record) throw new FleetApiError(`no deployment ${deploymentId}`, 404, deploymentId)
      return deploymentRecord(record)
    },

    relayExceptions: (deploymentId) =>
      readOne<RelayExceptions>(url(CONSOLE_API_PATHS.relayExceptions(deploymentId))),

    setExceptionUpload: async (deploymentId, on) => {
      const profile = await send<{ exceptionUpload: boolean }>(url(CONSOLE_API_PATHS.relayProfile(deploymentId)), {
        method: 'PUT',
        body: JSON.stringify({ exceptionUpload: on }),
      })
      if (!profile) throw new FleetApiError(`no deployment ${deploymentId}`, 404, deploymentId)
      return profile.exceptionUpload
    },

    issueRelayCommand: async (deploymentId, { kind, confirm = false, reason }) => {
      const command = await send<RelayCommand>(url(CONSOLE_API_PATHS.relayCommands(deploymentId)), {
        method: 'POST',
        body: JSON.stringify({
          kind,
          ...(kind === 'discard-exceptions' ? { confirm } : {}),
          ...reasonField(reason),
        }),
      })
      if (!command) throw new FleetApiError(`no deployment ${deploymentId}`, 404, deploymentId)
      return command
    },

    relayTelemetryCommands: (deploymentId) =>
      readList<RelayTelemetryCommand>(url(CONSOLE_API_PATHS.relayTelemetryPause(deploymentId))),

    issueTelemetryCommand: async (deploymentId, { reason, ...request }) => {
      const command = await send<RelayTelemetryCommand>(url(CONSOLE_API_PATHS.relayTelemetryPause(deploymentId)), {
        method: 'POST',
        body: JSON.stringify({ ...request, ...reasonField(reason) }),
      })
      if (!command) throw new FleetApiError(`no deployment ${deploymentId}`, 404, deploymentId)
      return command
    },

    listRelayDirectives: (deploymentId) =>
      readList<RelayDirective>(url(CONSOLE_API_PATHS.relayDirectives(deploymentId))),

    setRelayCadence: async (deploymentId, { telemetryCadenceSeconds, minSendIntervalSeconds }) => {
      const cadence = await send<RelayCadence>(url(CONSOLE_API_PATHS.relayCadence(deploymentId)), {
        method: 'PUT',
        body: JSON.stringify({ telemetryCadenceSeconds, minSendIntervalSeconds }),
      })
      if (!cadence) throw new FleetApiError(`no deployment ${deploymentId}`, 404, deploymentId)
      return cadence
    },

    listExceptionUploads: (deploymentId) =>
      readList<RelayExceptionUpload>(url(CONSOLE_API_PATHS.relayUploads(deploymentId))),

    downloadExceptionUpload: (deploymentId, uploadId) =>
      downloadBlob(url(CONSOLE_API_PATHS.relayUpload(deploymentId, uploadId))),

    // -----------------------------------------------------------------------------------
    // The client half (wf-12 U-23). Seven routes, one per method, no route called twice.
    // -----------------------------------------------------------------------------------

    listClients: () => readList<ClientRecord>(url(CONSOLE_API_PATHS.clients)),

    // Null rather than a throw, and the same null for "no such client" and "not yours". The
    // server answers 404 for both on purpose and the console must not pretend to tell them
    // apart (wf-12 U-20).
    getClient: (clientId) => send<ClientRecord>(url(CONSOLE_API_PATHS.client(clientId))),

    clientTelemetrySummary: (clientId) =>
      readList<RelayTelemetrySummary>(url(CONSOLE_API_PATHS.telemetrySummary(clientId))),

    createClient: async (input: ClientCreate) => {
      const record = await send<ClientRecord>(url(CONSOLE_API_PATHS.clients), {
        method: 'POST',
        body: JSON.stringify(input),
      })
      // A 201 with no body would be a server that created something and told us nothing, and
      // the screen's next act is to show the operator the record it just made.
      if (!record) throw new FleetApiError('console-api created no client record', 201, CONSOLE_API_PATHS.clients)
      return record
    },

    patchClient: async (clientId, patch: ClientPatch) => {
      const record = await send<ClientRecord>(url(CONSOLE_API_PATHS.client(clientId)), {
        method: 'PATCH',
        // `patchBody`, not the object as given: only the fields the caller actually set are
        // sent, which is how one axis moves without the other (wf-01 U-21).
        body: JSON.stringify(patchBody(patch)),
      })
      if (!record) throw new FleetApiError(`no client ${clientId}`, 404, clientId)
      return record
    },

    clientReadiness: (clientId) => send<ClientReadiness>(url(CONSOLE_API_PATHS.readiness(clientId))),

    holdClient: async (clientId, reason) => {
      const record = await send<ClientRecord>(
        url(CONSOLE_API_PATHS.hold(clientId)),
        reason === null ? { method: 'DELETE' } : { method: 'PUT', body: JSON.stringify({ reason }) },
      )
      if (!record) throw new FleetApiError(`no client ${clientId}`, 404, clientId)
      return record
    },

    offboardClient: async (clientId, reason) => {
      const record = await send<ClientRecord>(
        url(CONSOLE_API_PATHS.offboarding(clientId)),
        reason === null ? { method: 'DELETE' } : { method: 'PUT', body: JSON.stringify({ reason }) },
      )
      if (!record) throw new FleetApiError(`no client ${clientId}`, 404, clientId)
      return record
    },

    issueBundle: async (clientId, options = {}) => {
      const { installId, environmentClass, forReactivation } = options
      const bundle = await send<IssuedBundle>(url(CONSOLE_API_PATHS.bundles(clientId)), {
        method: 'POST',
        body: JSON.stringify({
          ...(installId === undefined ? {} : { installId }),
          ...(environmentClass === undefined ? {} : { environmentClass }),
          ...(forReactivation === undefined ? {} : { forReactivation }),
        }),
      })
      // The one call in this file where an empty result would be actively dangerous: the token
      // is returned once and is a hash on our side, so a caller that treated "no body" as
      // "nothing happened" would have burned a bundle it can never read.
      if (!bundle) throw new FleetApiError(`no client ${clientId}`, 404, clientId)
      return bundle
    },

    listBundles: (clientId) => readList<BundleSummary>(url(CONSOLE_API_PATHS.bundles(clientId))),

    archiveBundle: async (clientId, bundleId) => {
      const archived = await send<BundleSummary>(url(CONSOLE_API_PATHS.bundle(clientId, bundleId)), {
        method: 'PATCH',
        body: JSON.stringify({ archived: true }),
      })
      if (!archived) throw new FleetApiError(`no bundle ${bundleId}`, 404, bundleId)
    },

    listInstalls: (clientId) => readList<InstallRecord>(url(CONSOLE_API_PATHS.installs(clientId))),

    createInstall: async (clientId, input: InstallCreate) => {
      const record = await send<InstallRecord>(url(CONSOLE_API_PATHS.installs(clientId)), {
        method: 'POST',
        // Only what the operator set (JSON drops an `undefined` key); the server's defaults are
        // `prod`, `customer-deploy` and `nexus`. A managed application's naming goes as given.
        body: JSON.stringify(input),
      })
      if (!record) throw new FleetApiError('console-api created no install record', 201, clientId)
      return record
    },

    archiveInstall: async (clientId, installId) => {
      const record = await send<InstallRecord>(url(CONSOLE_API_PATHS.install(clientId, installId)), {
        method: 'PATCH',
        body: JSON.stringify({ archived: true }),
      })
      if (!record) throw new FleetApiError(`no install ${installId}`, 404, installId)
      return record
    },

    generateInstallPackage: async (clientId: string, input: InstallPackageRequest): Promise<InstallPackageResult> => {
      const result = await send<InstallPackageResult>(url(CONSOLE_API_PATHS.installPackage(clientId)), {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if (!result) throw new FleetApiError('install package generation failed', 500, clientId)
      return result
    },

    downloadInstallPackageZip: (clientId: string, input: InstallPackageRequest): Promise<Blob> =>
      downloadZip(CONSOLE_API_PATHS.installPackageDownload(clientId), input, clientId),

    generateUpgradePackage: async (clientId: string, input: UpgradePackageRequest): Promise<UpgradePackageResult> => {
      const result = await send<UpgradePackageResult>(url(CONSOLE_API_PATHS.upgradePackage(clientId)), {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if (!result) throw new FleetApiError('upgrade package generation failed', 500, clientId)
      return result
    },

    downloadUpgradePackageZip: (clientId: string, input: UpgradePackageRequest): Promise<Blob> =>
      downloadZip(CONSOLE_API_PATHS.upgradePackageDownload(clientId), input, clientId),

    downloadUninstallPackageZip: (clientId: string, input: UninstallPackageRequest): Promise<Blob> =>
      downloadZip(CONSOLE_API_PATHS.uninstallPackageDownload(clientId), input, clientId),

    listManagedAppReleases: async (): Promise<ManagedAppReleaseRecord[]> => {
      const body = await readOne<{ releases?: ManagedAppReleaseRecord[] }>(url(CONSOLE_API_PATHS.managedAppReleases))
      // No list is a fault, never "nothing to choose", which would read as a Fleet with no release
      // a deployment could be pointed at or a package issued for.
      if (!Array.isArray(body.releases)) {
        throw new FleetApiError('the managed-application release list is missing', 500, CONSOLE_API_PATHS.managedAppReleases)
      }
      // `notes`, `bicepPackage` and `prerelease` are mapped rather than read: a console-api that
      // predates one sends no key, read as empty, as no Bicep package and as no beta, as the
      // contract defaults them.
      return body.releases.map((release) => ({
        ...release,
        notes: typeof release.notes === 'string' ? release.notes : '',
        bicepPackage: release.bicepPackage === true,
        prerelease: release.prerelease === true,
      }))
    },

    listReleaseNotices: async (version: string): Promise<ReleaseNoticeRecord[]> => {
      const target = CONSOLE_API_PATHS.releaseNotices(version)
      const body = await send<{ notices?: ReleaseNoticeRecord[] }>(url(target))
      // A 404 is a release not on the list, never "nobody was notified", which is an empty list.
      if (!body) throw new FleetApiError(`No release ${version} on the managed-application list`, 404, target)
      if (!Array.isArray(body.notices)) throw new FleetApiError('the release notice list is missing', 500, target)
      return body.notices.map(noticeOf)
    },

    listFleetReleaseNotices: async (): Promise<FleetReleaseNoticeRecord[]> => {
      const target = CONSOLE_API_PATHS.fleetReleaseNotices
      const body = await readOne<{ notices?: FleetReleaseNoticeRecord[] }>(url(target))
      // No list is a fault, never "nobody was offered anything", which is an empty one.
      if (!Array.isArray(body.notices)) throw new FleetApiError('the release notice list is missing', 500, target)
      return body.notices.map((n) => ({ ...noticeOf(n), version: n.version }))
    },

    notifyRelease: async (version: string, input: ReleaseNoticeSet): Promise<ReleaseNoticeResult> => {
      const target = CONSOLE_API_PATHS.releaseNotices(version)
      const result = await send<ReleaseNoticeResult>(url(target), {
        method: 'POST',
        // The deployments, how the kind is chosen, and edited notes: console-api reads the images,
        // and the notes when none are sent, from the release, and who notified from the bearer
        // token. Dropping `majorUpdate` here once stored every major offer as a minor one
        // (nth-ai/fleet-api#225), so each key is passed on when set.
        body: JSON.stringify({
          deploymentIds: input.deploymentIds,
          ...(input.majorUpdate ? { majorUpdate: true } : {}),
          ...(input.updateKind ? { updateKind: input.updateKind } : {}),
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
        }),
      })
      // On a write a 404 is a refusal of the whole notify: the release is not on the list. A
      // deployment that is unknown is refused on its own, inside a 200.
      if (!result) throw new FleetApiError(`No release ${version} on the managed-application list`, 404, target)
      if (!Array.isArray(result.notified) || !Array.isArray(result.refused)) {
        throw new FleetApiError('the notify result is missing its lists', 500, target)
      }
      return {
        ...result,
        notified: result.notified.map((n) => ({ ...noticeOf(n), imageOnly: typeof n.imageOnly === 'boolean' ? n.imageOnly : null })),
      }
    },

    previewReleaseOffer: async (version: string): Promise<OfferPreview> => {
      const target = CONSOLE_API_PATHS.offerPreview(version)
      const body = await readOne<OfferPreview>(url(target))
      if (!Array.isArray(body.deployments)) throw new FleetApiError('the offer preview is missing its list', 500, target)
      return body
    },

    // A 404 is "no target set" as often as it is "no such deployment", and console-api does not
    // tell them apart, so `send`'s null is passed through as the answer rather than thrown.
    getTargetRelease: async (deploymentId: string): Promise<TargetRelease | null> => {
      const record = await send<TargetRelease>(url(CONSOLE_API_PATHS.targetRelease(deploymentId)))
      return record ? targetReleaseOf(record) : null
    },

    clearTargetRelease: async (deploymentId: string): Promise<void> => {
      await send<void>(url(CONSOLE_API_PATHS.targetRelease(deploymentId)), { method: 'DELETE' })
    },

    declareMarketplacePlan: async (deploymentId: string, plan: MarketplacePlan): Promise<DeploymentMarketplacePlan> => {
      const target = CONSOLE_API_PATHS.marketplacePlan(deploymentId)
      const record = await send<Record<string, string | null | undefined>>(url(target), {
        method: 'PUT',
        // The kind and nothing else: who declared it is the bearer token's, never a body field.
        body: JSON.stringify({ plan }),
      })
      if (!record) throw new FleetApiError(`no deployment ${deploymentId}`, 404, target)
      // The same reading as the deployment record's: a kind this console does not know is `null`.
      return {
        marketplacePlanId: record.marketplacePlanId ?? null,
        marketplacePlan: marketplacePlanOf(record.marketplacePlan),
        declaredMarketplacePlan: marketplacePlanOf(record.declaredMarketplacePlan),
        declaredMarketplacePlanBy: record.declaredMarketplacePlanBy ?? null,
        declaredMarketplacePlanAt: record.declaredMarketplacePlanAt ?? null,
        effectiveMarketplacePlan: marketplacePlanOf(record.effectiveMarketplacePlan),
      }
    },

    // Idempotent on the server, as clearing a target is: a 204 whether or not one was declared.
    clearMarketplacePlan: async (deploymentId: string): Promise<void> => {
      await send<void>(url(CONSOLE_API_PATHS.marketplacePlan(deploymentId)), { method: 'DELETE' })
    },

    listManagedUpdates: async (deploymentId: string): Promise<ManagedUpdate[]> => {
      const target = CONSOLE_API_PATHS.managedUpdates(deploymentId)
      const body = await readOne<{ operations?: ManagedUpdate[] }>(url(target))
      // No list is a fault, never "nobody has asked for an update", which is an answer
      // console-api gives as an empty one.
      if (!Array.isArray(body.operations)) {
        throw new FleetApiError('the managed update list is missing', 500, target)
      }
      return body.operations
    },

    // A 404 is an operation that is unknown or not ours, which console-api does not tell apart;
    // `send`'s null is passed through and the polling screen says it can no longer read it.
    getManagedUpdate: (deploymentId: string, operationId: string) =>
      send<ManagedUpdate>(url(CONSOLE_API_PATHS.managedUpdate(deploymentId, operationId))),

    currentAccess: async () => {
      const record = await send<CurrentAccess>(url(CONSOLE_API_PATHS.currentAccess))
      if (!record) throw new FleetApiError('console access record is missing', 404, CONSOLE_API_PATHS.currentAccess)
      return record
    },

    listUsers: () => readList<ConsoleUser>(url(CONSOLE_API_PATHS.users)),

    inviteUser: async (input: UserInvite) => {
      const record = await send<ConsoleUser>(url(CONSOLE_API_PATHS.users), {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if (!record) throw new FleetApiError('created console user is missing', 404, CONSOLE_API_PATHS.users)
      return record
    },

    patchUser: async (userId, input: UserPatch) => {
      const target = CONSOLE_API_PATHS.user(userId)
      const record = await send<ConsoleUser>(url(target), { method: 'PATCH', body: JSON.stringify(input) })
      if (!record) throw new FleetApiError(`no console user ${userId}`, 404, target)
      return record
    },

    listRoles: () => readList<ConsoleRole>(url(CONSOLE_API_PATHS.roles)),
    listCapabilities: () => readList<string>(url(CONSOLE_API_PATHS.capabilities)),

    createRole: async (input: RoleCreate) => {
      const record = await send<ConsoleRole>(url(CONSOLE_API_PATHS.roles), {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if (!record) throw new FleetApiError('created console role is missing', 404, CONSOLE_API_PATHS.roles)
      return record
    },

    patchRole: async (roleId, input: RolePatch) => {
      const target = CONSOLE_API_PATHS.role(roleId)
      const record = await send<ConsoleRole>(url(target), { method: 'PATCH', body: JSON.stringify(input) })
      if (!record) throw new FleetApiError(`no console role ${roleId}`, 404, target)
      return record
    },

    assignRole: async (input: AssignmentCreate) => {
      const record = await send<RoleAssignment>(url(CONSOLE_API_PATHS.assignments), {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if (!record) throw new FleetApiError('created role assignment is missing', 404, CONSOLE_API_PATHS.assignments)
      return record
    },

    removeRoleAssignment: async (assignmentId) => {
      await send<void>(url(CONSOLE_API_PATHS.assignment(assignmentId)), { method: 'DELETE' })
    },

    listAudit: (limit = 200) =>
      readList<ConsoleAuditEvent>(url(CONSOLE_API_PATHS.audit, { limit: String(limit) })),

    // `limit` caps at 500 server-side. This is the highest-volume table in the fleet plane by an
    // order of magnitude - a hundred deployments beating every two minutes is ~72,000 rows a day
    // before any fetch or ack - which is why the narrowing happens HERE and not in the component.
    //
    // `since`/`until`/`rejectedOnly` go to fleet-api rather than being applied to the fetched
    // page, and the difference is not style: filtering 200 fetched rows answers "which of the
    // newest 200 were rejections", and an operator asking for rejections means "which of them
    // all". A client-side filter over a server-side cap silently reports a subset as the set.
    //
    // `rejectedOnly` is sent only when true. `rejectedOnly=false` and an absent parameter mean
    // the same thing to the endpoint, and a URL carrying the default is a URL that looks like a
    // deliberate choice in a log.
    listFleetAudit: (query = {}) =>
      readList<FleetAuditEvent>(url(CONSOLE_API_PATHS.fleetAudit, {
        limit: String(query.limit ?? 200),
        deploymentId: query.deploymentId,
        routeTemplate: query.routeTemplate,
        since: query.since,
        until: query.until,
        rejectedOnly: query.rejectedOnly ? 'true' : undefined,
      })),

    // `send` already answers null on a 404, which is exactly the shape this needs: "crossed, no
    // longer held" is a real answer and travels as an absence rather than as a throw. Note the
    // 404 is documented in the endpoint's own description but is NOT in its declared `responses`
    // - reported to fleet-api rather than worked around, because a client generated from that
    // spec would not know the case exists.
    /**
     * Asks for the server's own cap rather than a smaller page, for the same reason the audit
     * list does: a page that came back FULL has to be distinguishable from a complete history,
     * and a short limit hides truncation at exactly the moment it misleads.
     */
    listDelivery: (deploymentId: string, limit = 500) =>
      readList<DeliveryAttempt>(url(CONSOLE_API_PATHS.delivery(deploymentId), { limit: String(limit) })),

    listTelemetry: (deploymentId: string, cursor?: string, limit = 50) =>
      readOne<RelayTelemetryPage>(
        url(CONSOLE_API_PATHS.telemetry(deploymentId), {
          limit: String(limit),
          cursor,
        }),
      ),

    fetchPayload: (contentHash: string) =>
      send<FleetPayload>(url(CONSOLE_API_PATHS.fleetPayload(contentHash))),
  }
}
